/**
 * <SeoHead /> — Single source of truth for SEO meta tags on the public site.
 *
 * Web adaptation (LOTE M — Full Stack migration). The original component used
 * `<Head>` from `expo-router/head` to inject tags into the document <head>
 * during static rendering (SSG). In the React Web version we update
 * `document.head` imperatively via `useEffect`. This is functionally
 * equivalent for Google (which executes JS) and is complemented by static
 * defaults baked into `public/index.html` so social link unfurlers
 * (WhatsApp, LinkedIn, X, Facebook, Slack, Discord) still get a good
 * initial preview before JS runs.
 *
 * IMPORTANT: This is meant ONLY for routes inside the public marketing
 * site. Do not import this from app-internal pages to keep the private
 * surface area isolated from public SEO.
 */
import React, { useEffect } from 'react';
import { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE, pageSeo, pageUrl, WwwPage } from './seo';

type Props = {
  page: WwwPage;
  /** Language used to set the OG locale. Defaults to 'es' since that is the
   *  language of the initial paint (matches `(www)/_layout.tsx`). */
  locale?: 'es' | 'en' | 'de';
  /** Optional override for Open Graph image (absolute URL). */
  ogImage?: string;
  /** Inject extra JSON-LD structured data (Schema.org). */
  jsonLd?: object;
};

const OG_LOCALE: Record<'es' | 'en' | 'de', string> = {
  es: 'es_ES',
  en: 'en_US',
  de: 'de_DE',
};

// Data-attribute used to tag every <meta>/<link>/<script> we manage, so we can
// clean them up cleanly on route change or unmount without touching the
// static tags that live in public/index.html.
const SEO_MARK = 'mf-seo';

export function SeoHead({ page, locale = 'es', ogImage, jsonLd }: Props) {
  const { title, description } = pageSeo(page, locale);
  const url = pageUrl(page);
  const image = ogImage || DEFAULT_OG_IMAGE;

  useEffect(() => {
    // Remove any SEO tags we managed previously to avoid duplicates.
    document.head.querySelectorAll(`[data-${SEO_MARK}]`).forEach((el) => el.remove());

    // <title>
    document.title = title;

    // Helpers to create tagged elements. For single-value tags we first
    // remove any pre-existing same-name/same-property tag from the static
    // index.html (e.g. the default <meta name="description">) so we don't
    // end up with two competing values in the head — search crawlers and
    // social unfurlers tend to pick the FIRST match, which would otherwise
    // be the placeholder. For multi-value tags (og:locale:alternate,
    // hreflang) we pass multi=true so the 2nd/3rd/4th calls don't wipe
    // out the previous additions.
    const addMeta = (attr: 'name' | 'property', key: string, content: string, multi = false) => {
      if (!multi) {
        document.head.querySelectorAll(`meta[${attr}="${key}"]`).forEach((el) => el.remove());
      }
      const el = document.createElement('meta');
      el.setAttribute(attr, key);
      el.setAttribute('content', content);
      el.setAttribute(`data-${SEO_MARK}`, '');
      document.head.appendChild(el);
    };
    const addLink = (rel: string, href: string, hrefLang?: string, multi = false) => {
      if (!multi) {
        document.head.querySelectorAll(`link[rel="${rel}"]:not([rel="preconnect"]):not([rel="stylesheet"])`).forEach((el) => el.remove());
      }
      const el = document.createElement('link');
      el.setAttribute('rel', rel);
      el.setAttribute('href', href);
      if (hrefLang) el.setAttribute('hreflang', hrefLang);
      el.setAttribute(`data-${SEO_MARK}`, '');
      document.head.appendChild(el);
    };

    // ============== Primary ==============
    addMeta('name', 'description', description);
    addMeta('name', 'robots', 'index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1');
    addMeta('name', 'googlebot', 'index,follow');
    addLink('canonical', url);

    // ============== Open Graph ==============
    addMeta('property', 'og:type', 'website');
    addMeta('property', 'og:site_name', SITE_NAME);
    addMeta('property', 'og:title', title);
    addMeta('property', 'og:description', description);
    addMeta('property', 'og:url', url);
    addMeta('property', 'og:image', image);
    addMeta('property', 'og:image:width', '1200');
    addMeta('property', 'og:image:height', '630');
    addMeta('property', 'og:locale', OG_LOCALE[locale]);
    addMeta('property', 'og:locale:alternate', 'es_ES', true);
    addMeta('property', 'og:locale:alternate', 'en_US', true);
    addMeta('property', 'og:locale:alternate', 'de_DE', true);

    // ============== Twitter / X ==============
    addMeta('name', 'twitter:card', 'summary_large_image');
    addMeta('name', 'twitter:title', title);
    addMeta('name', 'twitter:description', description);
    addMeta('name', 'twitter:image', image);

    // ============== App / Browser hints ==============
    addMeta('name', 'application-name', SITE_NAME);
    addMeta('name', 'apple-mobile-web-app-title', SITE_NAME);
    addMeta('name', 'apple-mobile-web-app-capable', 'yes');
    addMeta('name', 'apple-mobile-web-app-status-bar-style', 'black-translucent');
    addMeta('name', 'theme-color', '#09090B');
    addMeta('name', 'color-scheme', 'dark');
    addMeta('name', 'format-detection', 'telephone=no');

    // ============== Hreflang ==============
    // Site uses client-side language detection on the same URL, so we point
    // every hreflang to the same path. `x-default` is the fallback Google
    // uses when no locale matches.
    addLink('alternate', url, 'es', true);
    addLink('alternate', url, 'en', true);
    addLink('alternate', url, 'de', true);
    addLink('alternate', url, 'x-default', true);

    // ============== JSON-LD ==============
    // The original expo-router/head version could not inject JSON-LD due to
    // react-helmet-async's <script> filter. In the React Web adaptation we
    // can inject it directly via document.createElement('script'), so the
    // structured data passed through the `jsonLd` prop is now honoured.
    if (jsonLd) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(jsonLd);
      script.setAttribute(`data-${SEO_MARK}`, '');
      document.head.appendChild(script);
    }

    // Cleanup on unmount: remove every tag we added.
    return () => {
      document.head.querySelectorAll(`[data-${SEO_MARK}]`).forEach((el) => el.remove());
    };
  }, [title, description, url, image, locale, jsonLd]);

  return null;
}

/** Pre-built JSON-LD blocks for the home page. */
export function organizationJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/icon.png`,
    email: 'support@stilgia.com',
    sameAs: [], // Add social profiles here when available
  };
}

export function websiteJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    inLanguage: ['es-ES', 'en-US', 'de-DE'],
  };
}

export function softwareApplicationJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'MobileApplication',
    name: SITE_NAME,
    operatingSystem: 'iOS, Android',
    applicationCategory: 'LifestyleApplication',
    description:
      'AI digital wardrobe with virtual try-ons, personal stylist and daily outfit recommendations.',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'EUR',
      description: 'Permanent free plan available',
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      email: 'support@stilgia.com',
    },
  };
}
